/**
 * Accounting · Refunds
 *
 * Money going back out, over a date range: what it cost, what share of revenue
 * it was, why it happened, which programs and pharmacies it came from, and the
 * individual refunds behind all of it.
 *
 * The refund rate uses the same revenue this app's P&L reports, so the number
 * reconciles against the P&L rather than floating free.
 *
 * - Single clinic: rate vs revenue + reason mix + program/pharmacy + line items
 * - All Clinics (aggregate): the same, rolled up, with clinics that have no
 *   refund feed named explicitly rather than folded in as zeros
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { format, parse } from 'date-fns'
import { Card, CardHeader, CardTitle, CardContent, Button } from '../components/ui'
import ChannelSplitCard from '../components/ChannelSplitCard'
import AttributionSettings, { DEFAULT_ENGINE } from '../components/AttributionSettings'
import { DEFAULT_LOOKBACK } from '../lib/attributionLookback'
import { DateRangePicker } from '../components/DateRangePicker'
import { accountingAPI } from '../services/api'
import { useAuthStore } from '../stores/authStore'
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import {
  Undo2,
  Percent,
  Receipt,
  Clock,
  RefreshCw,
  AlertCircle,
  AlertTriangle,
  Info,
  Download,
  Loader2,
  Repeat,
} from 'lucide-react'

const AUTO_REFRESH_INTERVAL = 15 * 60 * 1000 // 15 minutes

const getDefaultDateRange = () => {
  const end = new Date()
  const start = new Date()
  start.setDate(start.getDate() - 29)
  return { from: start, to: end }
}

function formatCurrency(cents, compact = false) {
  const dollars = (cents || 0) / 100
  if (compact && Math.abs(dollars) >= 1000) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency: 'USD', notation: 'compact', maximumFractionDigits: 1
    }).format(dollars)
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0
  }).format(dollars)
}

function formatNumber(num) {
  return new Intl.NumberFormat('en-US').format(num || 0)
}

function formatPercent(value) {
  if (value === null || value === undefined) return '—'
  return `${value}%`
}

// Distinct hues per reason bucket so the mix bar and the table agree at a
// glance. Deliberately not the primary brand color — refunds are not a
// celebration.
// Keys are Odin's refund reason codes (odin lib/refundReasons.ts), plus the
// buckets older free-text reasons and processor defaults fall into.
const REASON_COLORS = {
  disqualified: '#8B5CF6',
  did_not_ship: '#F97316',
  patient_cancelled: '#EF4444',
  plan_change: '#3B82F6',
  duplicate_charge: '#F59E0B',
  price_adjustment: '#10B981',
  test_order: '#64748B',
  fraud: '#BE123C',
  service: '#14B8A6',
  not_recorded: '#CBD5E1',
  other: '#94A3B8',
}

function reasonColor(key) {
  return REASON_COLORS[key] || REASON_COLORS.other
}

function SummaryCard({ title, value, subtitle, icon: Icon, loading, tone = 'default' }) {
  const iconTone = tone === 'warn'
    ? 'bg-destructive/10 text-destructive'
    : 'bg-primary/10 text-primary'

  return (
    <Card className="relative overflow-hidden">
      <CardContent className="p-6">
        {loading ? (
          <div className="animate-pulse space-y-3">
            <div className="h-4 bg-muted rounded w-1/2"></div>
            <div className="h-8 bg-muted rounded w-3/4"></div>
            <div className="h-3 bg-muted rounded w-1/3"></div>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-muted-foreground">{title}</span>
              {Icon && (
                <div className={`p-2 rounded-lg ${iconTone}`}>
                  <Icon className="h-4 w-4" />
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold tracking-tight">{value}</span>
            </div>
            {subtitle && (
              <div className="flex items-center gap-2 mt-1">
                <span className="text-xs text-muted-foreground">{subtitle}</span>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}

function RefundTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null
  const point = payload[0]?.payload || {}
  return (
    <div className="bg-popover border border-border rounded-lg shadow-lg p-3 min-w-[200px]">
      <p className="font-medium text-sm mb-2">{label}</p>
      <div className="space-y-1 text-xs">
        <div className="flex items-center justify-between gap-4">
          <span className="text-muted-foreground">Refunded</span>
          <span className="font-medium">{formatCurrency(point.refunds)}</span>
        </div>
        <div className="flex items-center justify-between gap-4">
          <span className="text-muted-foreground">Refunds</span>
          <span className="font-medium">{formatNumber(point.count)}</span>
        </div>
        <div className="flex items-center justify-between gap-4">
          <span className="text-muted-foreground">Revenue</span>
          <span className="font-medium">{formatCurrency(point.revenue)}</span>
        </div>
        <div className="flex items-center justify-between gap-4 pt-1 border-t border-border">
          <span className="text-muted-foreground">Rate</span>
          <span className="font-medium">{formatPercent(point.rate)}</span>
        </div>
      </div>
    </div>
  )
}

/**
 * Banner for stores that are configured but did not answer. Losing the
 * Hyperswitch slice while Chargebee still reports would otherwise look like a
 * quiet month rather than a partial one.
 */
function StoreWarnings({ stores }) {
  const failed = (stores || []).filter(s => !s.ok)
  if (!failed.length) return null

  return (
    <Card className="border-amber-500/50 bg-amber-500/5">
      <CardContent className="p-4">
        <div className="flex gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
          <div className="text-sm space-y-1 min-w-0">
            <p className="font-medium">Some refund sources did not report</p>
            {failed.map((s, i) => (
              <p key={i} className="text-muted-foreground text-xs break-words">
                <span className="font-medium capitalize">{s.store}</span>
                {s.deployment ? ` (${s.deployment}${s.tenant ? ` · ${s.tenant}` : ''})` : ''}: {s.error}
              </p>
            ))}
            <p className="text-xs text-muted-foreground pt-1">
              Totals below cover only the sources that responded.
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

function toCsv(items, isAggregateMode) {
  const headers = [
    ...(isAggregateMode ? ['Clinic'] : []),
    'Date', 'Amount', 'Status', 'Reason', 'Raw Reason', 'Refunded By', 'Bought Again (days)',
    'Program', 'Pharmacy', 'Customer', 'Email', 'Source', 'Connector', 'Refund ID',
  ]
  const escape = (value) => {
    const str = value === null || value === undefined ? '' : String(value)
    return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str
  }
  const rows = items.map(item => [
    ...(isAggregateMode ? [item.clinicName] : []),
    item.date,
    ((item.amount || 0) / 100).toFixed(2),
    item.status,
    item.reason_label,
    item.reason_raw,
    item.refunded_by,
    item.repurchase ? item.repurchase.days : '',
    item.program,
    item.pharmacy,
    item.customer_name,
    item.customer_email,
    item.store,
    item.connector,
    item.refund_id,
  ].map(escape).join(','))
  return [headers.join(','), ...rows].join('\n')
}

export default function RefundsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [lastRefresh, setLastRefresh] = useState(null)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [reasonFilter, setReasonFilter] = useState(null)

  const { currentClinicId } = useAuthStore()
  const isAggregateMode = currentClinicId === null

  const dateRange = useMemo(() => {
    const startParam = searchParams.get('start')
    const endParam = searchParams.get('end')
    if (startParam && endParam) {
      try {
        const from = parse(startParam, 'yyyy-MM-dd', new Date())
        const to = parse(endParam, 'yyyy-MM-dd', new Date())
        if (!isNaN(from.getTime()) && !isNaN(to.getTime())) return { from, to }
      } catch (e) {
        console.error('[REFUNDS] Error parsing date params:', e)
      }
    }
    return getDefaultDateRange()
  }, [searchParams])

  const setDateRange = useCallback((newRange) => {
    if (newRange?.from && newRange?.to) {
      setSearchParams(prev => {
        const params = new URLSearchParams(prev)
        params.set('start', format(newRange.from, 'yyyy-MM-dd'))
        params.set('end', format(newRange.to, 'yyyy-MM-dd'))
        return params
      })
    }
  }, [setSearchParams])

  const fetchData = useCallback(async (showRefreshIndicator = false) => {
    if (!dateRange?.from || !dateRange?.to) return
    try {
      if (showRefreshIndicator) setIsRefreshing(true)
      else setLoading(true)
      setError(null)

      const startDate = format(dateRange.from, 'yyyy-MM-dd')
      const endDate = format(dateRange.to, 'yyyy-MM-dd')

      const response = isAggregateMode
        ? await accountingAPI.getAggregateRefunds({ startDate, endDate })
        : await accountingAPI.getRefunds({ startDate, endDate })

      if (response.data?.success) {
        setData(response.data.data)
        setLastRefresh(new Date())
      } else {
        throw new Error(response.data?.error?.message || 'Failed to load refunds data')
      }
    } catch (err) {
      console.error('[REFUNDS] Error fetching data:', err)
      setError(err.response?.data?.error?.message || err.message || 'Failed to load refunds data')
    } finally {
      setLoading(false)
      setIsRefreshing(false)
    }
  }, [isAggregateMode, dateRange])

  // Refunds per acquisition channel. Fetched separately from the main report
  // because it only exists for Manifold-billed clinics — a core-billed clinic
  // has no refund records to split, and that is a 400 with a reason rather
  // than an error the whole page should show.
  const [bySource, setBySource] = useState(null)
  const [bySourceLoading, setBySourceLoading] = useState(false)
  const [bySourceError, setBySourceError] = useState(null)
  const [engine, setEngine] = useState(DEFAULT_ENGINE)
  const lookbackDays = DEFAULT_LOOKBACK // all time for every brand; not a choice on the page

  const fetchBySource = useCallback(async () => {
    if (isAggregateMode || !dateRange?.from || !dateRange?.to) {
      setBySource(null)
      return
    }
    try {
      setBySourceLoading(true)
      setBySourceError(null)
      const response = await accountingAPI.getRefundsBySource({
        startDate: format(dateRange.from, 'yyyy-MM-dd'),
        endDate: format(dateRange.to, 'yyyy-MM-dd'),
        engine,
        lookbackDays,
      })
      setBySource(response.data)
    } catch (err) {
      setBySource(null)
      setBySourceError(
        err.response?.data?.error?.message || err.message || 'Could not load the channel split'
      )
    } finally {
      setBySourceLoading(false)
    }
  }, [isAggregateMode, dateRange, engine, lookbackDays])

  useEffect(() => { fetchData() }, [fetchData])
  useEffect(() => { fetchBySource() }, [fetchBySource])

  useEffect(() => {
    const interval = setInterval(() => fetchData(true), AUTO_REFRESH_INTERVAL)
    return () => clearInterval(interval)
  }, [fetchData])

  const summary = data?.summary
  const byReason = data?.by_reason || []
  const byProgram = data?.by_program || []
  const byPharmacy = data?.by_pharmacy || []
  const allItems = data?.items || []
  const uncovered = data?.clinics_without_refund_data || []
  const attribution = data?.attribution

  const items = useMemo(
    () => (reasonFilter ? allItems.filter(i => i.reason === reasonFilter) : allItems),
    [allItems, reasonFilter]
  )

  const chartData = data?.daily || []

  const handleRefresh = () => fetchData(true)

  const handleExport = () => {
    const csv = toCsv(items, isAggregateMode)
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `refunds-${format(dateRange.from, 'yyyy-MM-dd')}-to-${format(dateRange.to, 'yyyy-MM-dd')}.csv`
    link.click()
    URL.revokeObjectURL(url)
  }

  if (error && !data) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold">Refunds</h1>
        </div>
        <Card className="p-8">
          <div className="flex flex-col items-center justify-center text-center">
            <AlertCircle className="h-12 w-12 text-destructive mb-4" />
            <h3 className="text-lg font-semibold mb-2">Failed to Load Report</h3>
            <p className="text-muted-foreground mb-4">{error}</p>
            <Button onClick={() => fetchData()}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </Button>
          </div>
        </Card>
      </div>
    )
  }

  // A clinic with no refund feed gets an explanation, not an empty report — a
  // zero here is indistinguishable from "no refunds happened".
  if (!loading && data && data.available === false) {
    return (
      <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
        <div className="flex items-center justify-between gap-2">
          <h1 className="text-lg sm:text-2xl font-bold">Refunds</h1>
          <DateRangePicker dateRange={dateRange} onDateRangeChange={setDateRange} />
        </div>
        <Card className="p-8">
          <div className="flex flex-col items-center justify-center text-center max-w-lg mx-auto">
            <Info className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No refund data for this clinic</h3>
            <p className="text-muted-foreground text-sm">{data.unavailable_reason}</p>
            <p className="text-muted-foreground text-xs mt-4">
              This is shown instead of a zero on purpose — an empty report here would look like
              a clinic with no refunds.
            </p>
          </div>
        </Card>
      </div>
    )
  }

  const maxReason = byReason.reduce((m, r) => Math.max(m, r.amount), 0)
  const maxProgram = byProgram.reduce((m, r) => Math.max(m, r.amount), 0)
  const maxPharmacy = byPharmacy.reduce((m, r) => Math.max(m, r.amount), 0)

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <h1 className="text-lg sm:text-2xl font-bold truncate">
            Refunds{isAggregateMode ? ' — All Brands' : ''}
          </h1>
          <p className="text-xs text-muted-foreground hidden sm:block">
            Refunded amounts against P&amp;L revenue · succeeded refunds only
            {lastRefresh && ` · ${lastRefresh.toLocaleTimeString()}`}
          </p>
        </div>
        <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">
          <DateRangePicker dateRange={dateRange} onDateRangeChange={setDateRange} />
          <Button
            variant="outline"
            size="icon"
            onClick={handleExport}
            disabled={loading || !items.length}
            title="Export line items as CSV"
            className="h-8 w-8 sm:h-9 sm:w-9"
          >
            <Download className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="h-8 w-8 sm:h-9 sm:w-9"
          >
            {isRefreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      <StoreWarnings stores={data?.stores} />

      {/* Revenue feed problems — the rate is hidden, so say why */}
      {(data?.revenue_warning || (data?.revenue_warnings || []).length > 0) && (
        <Card className="border-amber-500/50 bg-amber-500/5">
          <CardContent className="p-4">
            <div className="flex gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
              <div className="text-sm space-y-1 min-w-0">
                <p className="font-medium">Refund rate unavailable</p>
                {data.revenue_warning && (
                  <p className="text-xs text-muted-foreground">{data.revenue_warning}</p>
                )}
                {(data.revenue_warnings || []).map(w => (
                  <p key={w.clinicId} className="text-xs text-muted-foreground">
                    <span className="font-medium">{w.displayName}</span> — {w.warning}
                  </p>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Clinics excluded from the rollup for lack of a refund feed */}
      {isAggregateMode && uncovered.length > 0 && (
        <Card className="border-muted-foreground/30 bg-muted/30">
          <CardContent className="p-4">
            <div className="flex gap-3">
              <Info className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
              <div className="text-sm min-w-0">
                <p className="font-medium mb-1">
                  {uncovered.length} clinic{uncovered.length === 1 ? '' : 's'} not included
                </p>
                <div className="space-y-0.5">
                  {uncovered.map(c => (
                    <p key={c.clinicId} className="text-xs text-muted-foreground">
                      <span className="font-medium">{c.displayName}</span> — {c.reason}
                    </p>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-1.5">
                  Excluded rather than counted at $0, which would understate the group refund rate.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <SummaryCard
          title="Total Refunded"
          value={formatCurrency(summary?.total_refunds || 0)}
          subtitle={`${formatNumber(summary?.refund_count || 0)} settled refunds, dated when refunded. The Daily Report also leaves out full refunds within a day of the charge.`}
          icon={Undo2}
          loading={loading}
          tone="warn"
        />
        <SummaryCard
          title="Refund Rate"
          value={formatPercent(summary?.refund_rate)}
          subtitle={
            summary?.gross_revenue
              ? `of ${formatCurrency(summary.gross_revenue, true)} revenue`
              : 'revenue unavailable'
          }
          icon={Percent}
          loading={loading}
          tone="warn"
        />
        {/* Refunds where the same customer paid again within 14 days: plan or
            dose switches done as refund-and-recharge, and duplicates undone.
            Money went back, but the customer did not leave. */}
        <SummaryCard
          title="Customer Bought Again"
          value={formatNumber(summary?.repurchased_count || 0)}
          subtitle={`${formatCurrency(summary?.repurchased_amount || 0)} refunded · paid again within 14 days (switches, duplicates)`}
          icon={Repeat}
          loading={loading}
        />
        <SummaryCard
          title="Average Refund"
          value={formatCurrency(summary?.avg_refund || 0)}
          subtitle="per succeeded refund"
          icon={Receipt}
          loading={loading}
        />
        <SummaryCard
          title="Net Revenue"
          value={summary?.net_revenue !== null && summary?.net_revenue !== undefined
            ? formatCurrency(summary.net_revenue)
            : '—'}
          subtitle={
            summary?.pending_count
              ? `${formatNumber(summary.pending_count)} refunds not yet settled`
              : 'revenue less refunds'
          }
          icon={summary?.pending_count ? Clock : Receipt}
          loading={loading}
        />
      </div>

      {/* Daily refunds + rate */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-medium">Daily Refunds &amp; Rate</CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Bars are refunded dollars on the day the refund settled. The line is that day's
            refunds as a share of that day's revenue — on a payment cohort, so a refund of an
            older order lands on the refund date, not the original sale date.
          </p>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="h-[280px] flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <ComposedChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis
                  dataKey="displayDate"
                  tick={{ fontSize: 12 }}
                  tickLine={false}
                  axisLine={false}
                  interval={Math.floor(chartData.length / 8)}
                />
                <YAxis
                  yAxisId="amount"
                  tick={{ fontSize: 12 }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(value) => formatCurrency(value, true)}
                />
                <YAxis
                  yAxisId="rate"
                  orientation="right"
                  tick={{ fontSize: 12 }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(value) => `${value}%`}
                />
                <Tooltip content={<RefundTooltip />} />
                <Bar yAxisId="amount" dataKey="refunds" name="Refunded" fill="#EF4444" radius={[4, 4, 0, 0]} />
                <Line
                  yAxisId="rate"
                  type="monotone"
                  dataKey="rate"
                  name="Refund rate"
                  stroke="#F59E0B"
                  strokeWidth={2}
                  dot={false}
                />
              </ComposedChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[280px] flex items-center justify-center text-muted-foreground">
              No data available for selected period
            </div>
          )}
        </CardContent>
      </Card>

      {/* Reason mix */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-medium">Why Refunds Happened</CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Disqualified comes from the patient&apos;s DQ note when there is one, since the
            reason typed on the refund often says Cancellation or Did not ship instead.
            Otherwise it is the reason staff pick in Odin. Older refunds carry the reason typed
            into Odin&apos;s audit note instead (the refund itself only ever said
            &ldquo;service_unsatisfactory&rdquo;, Manifold&apos;s default, which counts as
            not recorded). Exact wording is on each line item. Click a reason to filter.
          </p>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="animate-pulse space-y-2">
              {[...Array(4)].map((_, i) => <div key={i} className="h-8 bg-muted rounded"></div>)}
            </div>
          ) : byReason.length > 0 ? (
            <div className="space-y-3">
              {/* Proportional mix bar */}
              <div className="flex h-2.5 rounded-full overflow-hidden bg-muted">
                {byReason.map(r => (
                  <div
                    key={r.reason}
                    style={{ width: `${r.pct}%`, backgroundColor: reasonColor(r.reason) }}
                    title={`${r.label}: ${formatCurrency(r.amount)} (${r.pct}%)`}
                  />
                ))}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-2 font-medium">Reason</th>
                      <th className="text-right py-2 px-2 font-medium">Amount</th>
                      <th className="text-right py-2 px-2 font-medium">Share</th>
                      <th className="text-right py-2 px-2 font-medium">Count</th>
                      <th className="text-right py-2 px-2 font-medium hidden sm:table-cell">Avg</th>
                    </tr>
                  </thead>
                  <tbody>
                    {byReason.map(r => (
                      <tr
                        key={r.reason}
                        onClick={() => setReasonFilter(reasonFilter === r.reason ? null : r.reason)}
                        className={`border-b border-muted/50 cursor-pointer hover:bg-muted/50 ${
                          reasonFilter === r.reason ? 'bg-muted/60' : ''
                        }`}
                      >
                        <td className="py-2 px-2">
                          <div className="flex items-center gap-2">
                            <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: reasonColor(r.reason) }} />
                            <span className="font-medium">{r.label}</span>
                          </div>
                          <div className="mt-1 h-1 bg-muted rounded-full overflow-hidden max-w-[160px]">
                            <div
                              className="h-full rounded-full"
                              style={{
                                width: maxReason > 0 ? `${Math.max(2, (r.amount / maxReason) * 100)}%` : '0%',
                                backgroundColor: reasonColor(r.reason),
                              }}
                            />
                          </div>
                        </td>
                        <td className="text-right py-2 px-2 font-semibold">{formatCurrency(r.amount)}</td>
                        <td className="text-right py-2 px-2 text-muted-foreground">{formatPercent(r.pct)}</td>
                        <td className="text-right py-2 px-2">{formatNumber(r.count)}</td>
                        <td className="text-right py-2 px-2 text-muted-foreground hidden sm:table-cell">
                          {formatCurrency(r.count > 0 ? Math.round(r.amount / r.count) : 0)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="py-8 text-center text-muted-foreground">
              No refunds for selected period
            </div>
          )}
        </CardContent>
      </Card>

      {/* Refunds per acquisition channel.
          Program and pharmacy answer "what was refunded"; this answers "which
          channel's customers refund", which is the one that changes where money
          gets spent. Manifold-billed clinics only — hidden entirely rather than
          shown empty for the clinics that have no refund records to split. */}
      {!isAggregateMode && (bySource || bySourceLoading || bySourceError) && (
        <ChannelSplitCard
          title="Refunds by Acquisition Channel"
          action={<AttributionSettings models={bySource?.attribution?.models} engine={engine} onEngineChange={setEngine} />}
          loading={bySourceLoading}
          error={bySourceError}
          coverage={bySource?.coverage}
          coverageNote={
            bySource
              ? 'Refunds are joined to the customer through the payment they reverse, then to the channel that acquired them. Amounts are the refunded value, not the original order.'
              : undefined
          }
          valueLabel="Refunded"
          rows={(bySource?.byChannel || []).map(row => ({
            channel: row.channel,
            value: row.amount,
            secondary: `${row.refunds} refund${row.refunds === 1 ? '' : 's'}`,
          }))}
          formatValue={formatCurrency}
          emptyMessage="No refunds in this period"
        />
      )}

      {/* Who refunded, from Odin's audit notes */}
      {(data?.by_staff || []).some(r => r.staff !== 'Not recorded') && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base font-medium">Who Refunded</CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              The staff member on each refund&apos;s Odin audit note. Refunds made outside Odin have none.
            </p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2">
              {data.by_staff.map(r => (
                <div key={r.staff} className="flex items-center justify-between text-sm gap-3">
                  <span className="truncate" title={r.staff}>{r.staff}</span>
                  <span className="shrink-0 tabular-nums text-muted-foreground">
                    {formatNumber(r.count)} · <span className="text-foreground font-medium">{formatCurrency(r.amount)}</span>
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Program / pharmacy attribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-base font-medium">Refunds by Program</CardTitle>
            {attribution && (
              <p className="text-xs text-muted-foreground mt-1">
                {formatPercent(attribution.match_rate)} of refunds matched back to a core order.
                {attribution.unmatched > 0 && ` ${formatNumber(attribution.unmatched)} unattributed.`}
              </p>
            )}
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="animate-pulse space-y-2">
                {[...Array(4)].map((_, i) => <div key={i} className="h-6 bg-muted rounded"></div>)}
              </div>
            ) : byProgram.length > 0 ? (
              <div className="space-y-2">
                {byProgram.slice(0, 12).map(p => (
                  <div key={p.program} className="flex items-center gap-3 text-sm">
                    <div className="flex-1 min-w-0">
                      <div className="truncate" title={p.program}>{p.program}</div>
                      <div className="mt-1 h-1 bg-muted rounded-full overflow-hidden">
                        <div
                          className="h-full bg-destructive/70 rounded-full"
                          style={{ width: maxProgram > 0 ? `${Math.max(2, (p.amount / maxProgram) * 100)}%` : '0%' }}
                        />
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-medium">{formatCurrency(p.amount)}</div>
                      <div className="text-xs text-muted-foreground">{formatNumber(p.count)}</div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-8 text-center text-muted-foreground text-sm">No data</div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base font-medium">Refunds by Pharmacy</CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Fulfilling pharmacy of the refunded order, from pharmacy → script → routing.
            </p>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="animate-pulse space-y-2">
                {[...Array(4)].map((_, i) => <div key={i} className="h-6 bg-muted rounded"></div>)}
              </div>
            ) : byPharmacy.length > 0 ? (
              <div className="space-y-2">
                {byPharmacy.slice(0, 12).map(p => (
                  <div key={p.pharmacy} className="flex items-center gap-3 text-sm">
                    <div className="flex-1 min-w-0">
                      <div className="truncate" title={p.pharmacy}>{p.pharmacy}</div>
                      <div className="mt-1 h-1 bg-muted rounded-full overflow-hidden">
                        <div
                          className="h-full bg-destructive/70 rounded-full"
                          style={{ width: maxPharmacy > 0 ? `${Math.max(2, (p.amount / maxPharmacy) * 100)}%` : '0%' }}
                        />
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-medium">{formatCurrency(p.amount)}</div>
                      <div className="text-xs text-muted-foreground">{formatNumber(p.count)}</div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-8 text-center text-muted-foreground text-sm">No data</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Per-clinic rollup (aggregate mode) */}
      {isAggregateMode && (data?.clinic_breakdown || []).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base font-medium">Refunds by Clinic</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {data.clinic_breakdown.map(c => (
                <div key={c.clinicId} className="border rounded-lg p-3 bg-muted/30">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: c.color }} />
                    <span className="font-medium text-sm">{c.displayName}</span>
                  </div>
                  <div className="text-xl font-bold">{formatCurrency(c.summary.total_refunds)}</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {formatNumber(c.summary.refund_count)} refunds · {formatPercent(c.summary.refund_rate)} of revenue
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Line items */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div>
              <CardTitle className="text-base font-medium">Refund Line Items</CardTitle>
              <p className="text-xs text-muted-foreground mt-1">
                Newest first. Includes refunds that have not settled, flagged by status.
                {data?.items_truncated && ` Showing the most recent ${formatNumber(allItems.length)} only.`}
              </p>
            </div>
            {reasonFilter && (
              <Button variant="outline" size="sm" onClick={() => setReasonFilter(null)}>
                Clear filter: {byReason.find(r => r.reason === reasonFilter)?.label || reasonFilter}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="animate-pulse space-y-2">
              {[...Array(6)].map((_, i) => <div key={i} className="h-8 bg-muted rounded"></div>)}
            </div>
          ) : items.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-2 font-medium">Date</th>
                    {isAggregateMode && <th className="text-left py-2 px-2 font-medium">Clinic</th>}
                    <th className="text-right py-2 px-2 font-medium">Amount</th>
                    <th className="text-left py-2 px-2 font-medium">Reason</th>
                    <th className="text-left py-2 px-2 font-medium hidden lg:table-cell">Program</th>
                    <th className="text-left py-2 px-2 font-medium hidden xl:table-cell">Pharmacy</th>
                    <th className="text-left py-2 px-2 font-medium hidden md:table-cell">Customer</th>
                    <th className="text-left py-2 px-2 font-medium hidden sm:table-cell">Source</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(item => (
                    <tr key={`${item.store}-${item.refund_id}`} className="border-b border-muted/50 hover:bg-muted/50">
                      <td className="py-2 px-2 whitespace-nowrap">
                        {item.date}
                        {item.status !== 'succeeded' && (
                          <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] bg-amber-500/15 text-amber-600 dark:text-amber-400">
                            {item.status}
                          </span>
                        )}
                      </td>
                      {isAggregateMode && (
                        <td className="py-2 px-2 whitespace-nowrap text-muted-foreground">{item.clinicName}</td>
                      )}
                      <td className="text-right py-2 px-2 font-semibold whitespace-nowrap">
                        {formatCurrency(item.amount)}
                      </td>
                      <td className="py-2 px-2">
                        <div className="flex items-center gap-1.5">
                          <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: reasonColor(item.reason) }} />
                          <span>{item.reason_label}</span>
                        </div>
                        {item.reason_source === 'dq_note' && (
                          <div className="text-xs text-muted-foreground" title={item.typed_reason ? `Typed in Odin as ${item.typed_reason}` : undefined}>
                            from the patient&apos;s DQ note
                          </div>
                        )}
                        {(item.refunded_by || item.repurchase) && (
                          <div className="text-xs text-muted-foreground">
                            {item.refunded_by && <span>by {item.refunded_by.split('@')[0]}</span>}
                            {item.repurchase && (
                              <span
                                className="ml-1.5 inline-flex rounded px-1 bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
                                title={`Same customer paid ${formatCurrency(item.repurchase.amount)} ${Math.abs(item.repurchase.days)} days ${item.repurchase.days < 0 ? 'before' : 'after'} this refund`}
                              >
                                bought again
                              </span>
                            )}
                          </div>
                        )}
                        {item.reason_raw && item.reason_raw !== item.reason_label && (
                          <div className="text-xs text-muted-foreground truncate max-w-[220px]" title={item.reason_raw}>
                            {item.reason_raw}
                          </div>
                        )}
                      </td>
                      <td className="py-2 px-2 hidden lg:table-cell text-muted-foreground">
                        <span className="truncate block max-w-[200px]" title={item.program || ''}>
                          {item.program || '—'}
                        </span>
                      </td>
                      <td className="py-2 px-2 hidden xl:table-cell text-muted-foreground">
                        {item.pharmacy || '—'}
                      </td>
                      <td className="py-2 px-2 hidden md:table-cell text-muted-foreground">
                        <span className="truncate block max-w-[200px]" title={item.customer_email || ''}>
                          {item.customer_name || item.customer_email || '—'}
                        </span>
                      </td>
                      <td className="py-2 px-2 hidden sm:table-cell text-muted-foreground capitalize">
                        {item.store}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="py-8 text-center text-muted-foreground">
              {reasonFilter ? 'No refunds match this reason' : 'No refunds for selected period'}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
